import { ReactNode, useMemo, useState, useEffect, useRef } from "react";
import { api } from "../../convex/_generated/api";
import { useCachedQuery } from "@/hooks/use-cached-query";
import { warmQueryMemoryFromDexie } from "@/lib/query-memory-cache";
import { SHARED_CACHE, WARMUP_ENTRIES } from "@/lib/cache-config";
import { getCurrentNightWindow } from "@/lib/night-window";

/**
 * DataPreloader preloads all shared data needed across the application on
 * initial page load using Dexie (IndexedDB) for instant, offline-capable reads.
 *
 * Cached data is served instantly while fresh data revalidates in background.
 * Cache keys, tables, and TTLs live in src/lib/cache-config.ts — page
 * components consume the same entries, so nothing is fetched twice.
 *
 * User-specific queries (getMyReports, getUserPreferences, private locations)
 * are intentionally NOT preloaded to prevent data leakage between users.
 */
// Round time to the hour so cache keys and query args stay stable for an hour
function getTimeBucket(timestamp: number): number {
    return Math.floor(timestamp / (60 * 60 * 1000)) * (60 * 60 * 1000);
}

export function DataPreloader({ children }: { children: ReactNode }) {
    // Use a time bucket that updates every hour to keep cache keys in sync
    const [nowBucket, setNowBucket] = useState(() => getTimeBucket(Date.now()));

    // Update the time bucket every hour, aligned to hour boundaries
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const updateBucket = () => {
            setNowBucket(getTimeBucket(Date.now()));
        };

        // Calculate milliseconds until next hour boundary
        const now = Date.now();
        const nextHour = getTimeBucket(now) + (60 * 60 * 1000);
        const msUntilNextHour = nextHour - now;

        // Schedule first update at the next hour boundary
        const timeout = setTimeout(() => {
            updateBucket();
            // Then start repeating interval for subsequent hours
            intervalRef.current = setInterval(updateBucket, 60 * 60 * 1000);
        }, msUntilNextHour);

        return () => {
            clearTimeout(timeout);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    // Calculate time ranges for advanced dashboard - preload all common time periods
    // Extend endTime to include future (satellite + WSA forecast merge)
    const timeRanges = useMemo(() => {
        const now = nowBucket;
        const hours24 = 24 * 60 * 60 * 1000;
        const hours48 = 48 * 60 * 60 * 1000;
        const hours7d = 7 * 24 * 60 * 60 * 1000;
        const hours3d = 3 * 24 * 60 * 60 * 1000;

        return {
            "24h": {
                startTime: now - hours24,
                endTime: now + hours24,
            },
            "48h": {
                startTime: now - hours48,
                endTime: now + hours48,
            },
            "7d": {
                startTime: now - hours7d,
                endTime: now + hours7d,
            },
            "3d": {
                startTime: now,
                endTime: now + hours3d,
            },
        };
    }, [nowBucket]);

    // Hydrate the in-memory cache from IndexedDB on startup so every tab
    // switch after a reload is instant (and works fully offline).
    useEffect(() => {
        void warmQueryMemoryFromDexie(
            WARMUP_ENTRIES.map(({ table, cacheKey }) => ({ table, queryKey: cacheKey })),
        );
    }, []);

    // Preload all shared queries with Dexie caching.

    // Dashboard, WSA-ENLIL overlay, map current-conditions
    useCachedQuery(
        api.satelliteData.getRecentSatelliteData,
        { limit: 300 },
        SHARED_CACHE.satelliteRecent300,
    );

    // Advanced Dashboard - time ranges with downsampled chart-only data
    useCachedQuery(
        api.satelliteData.getSatelliteDataByTimeRange,
        { startTime: timeRanges["24h"].startTime, endTime: timeRanges["24h"].endTime, maxPoints: 1200 },
        SHARED_CACHE.satelliteRange24h,
    );
    useCachedQuery(
        api.satelliteData.getSatelliteDataByTimeRange,
        { startTime: timeRanges["48h"].startTime, endTime: timeRanges["48h"].endTime, maxPoints: 1200 },
        SHARED_CACHE.satelliteRange48h,
    );
    useCachedQuery(
        api.satelliteData.getSatelliteDataByTimeRange,
        { startTime: timeRanges["7d"].startTime, endTime: timeRanges["7d"].endTime, maxPoints: 1200 },
        SHARED_CACHE.satelliteRange7d,
    );

    // Advanced Dashboard - WSA forecast data (merged with satellite for seamless chart)
    useCachedQuery(
        api.wsaEnlil.getWSAForecastDataByTimeRange,
        { startTime: timeRanges["24h"].startTime, endTime: timeRanges["24h"].endTime },
        SHARED_CACHE.wsaForecastRange24h,
    );
    useCachedQuery(
        api.wsaEnlil.getWSAForecastDataByTimeRange,
        { startTime: timeRanges["48h"].startTime, endTime: timeRanges["48h"].endTime },
        SHARED_CACHE.wsaForecastRange48h,
    );
    useCachedQuery(
        api.wsaEnlil.getWSAForecastDataByTimeRange,
        { startTime: timeRanges["7d"].startTime, endTime: timeRanges["7d"].endTime },
        SHARED_CACHE.wsaForecastRange7d,
    );

    // WSA-ENLIL page - model forecast chart samples
    useCachedQuery(
        api.wsaEnlil.getEarthDataForCharts,
        { sampleSize: 4000 },
        SHARED_CACHE.wsaEarthData4000,
    );

    // Text forecast - smaller WSA sample
    useCachedQuery(
        api.wsaEnlil.getEarthDataForCharts,
        { sampleSize: 600 },
        SHARED_CACHE.wsaEarthData600,
    );

    // Hour strip + long-term forecast - hourly aggregated forecast (next 72h)
    useCachedQuery(
        api.wsaEnlil.getHourlyForecastForTimeRange,
        { startTime: timeRanges["3d"].startTime, endTime: timeRanges["3d"].endTime },
        SHARED_CACHE.wsaHourly72h,
    );

    // Long-term forecast - model-run metadata (freshness banner)
    useCachedQuery(api.wsaEnlil.getLatestModelRun, {}, SHARED_CACHE.wsaLatestModelRun);

    // Map + dashboard - latest satellite reading
    useCachedQuery(api.satelliteData.getLatestSatelliteData, {}, SHARED_CACHE.satelliteLatest);

    // Community aurora reports (capped feed — see aurora.getAuroraReportsWithUsers)
    useCachedQuery(
        api.aurora.getAuroraReportsWithUsers,
        { limit: 150 },
        SHARED_CACHE.auroraReportsAll,
    );

    // Map cloud overlay for the current night window (stable for the hour)
    const nightWindow = useMemo(() => {
        void nowBucket;
        const { startTime, endTime } = getCurrentNightWindow();
        return { startTime, endTime };
    }, [nowBucket]);
    useCachedQuery(
        api.aurora.getCloudReportsForMap,
        nightWindow,
        SHARED_CACHE.cloudReportsMap,
    );

    // Map - public viewing locations
    useCachedQuery(api.locations.getPublicLocations, {}, SHARED_CACHE.publicLocations);

    // D-index ML model artifacts - required to compute forecasts offline
    useCachedQuery(api.dIndexModel.getActiveModels, {}, SHARED_CACHE.dIndexActiveModels);

    // Space weather: sunspots, CME predictions, magnetometer indices, HPI
    useCachedQuery(api.spaceWeather.getLatestSolarRegions, {}, SHARED_CACHE.spaceWeatherRegions);
    useCachedQuery(
        api.spaceWeather.getUpcomingCmePredictions,
        { limit: 8, nowMs: nowBucket },
        SHARED_CACHE.spaceWeatherCmes,
    );
    useCachedQuery(
        api.spaceWeather.getLatestMagnetometerSnapshot,
        {},
        SHARED_CACHE.spaceWeatherMagnetometer,
    );
    useCachedQuery(
        api.spaceWeather.getRecentHemisphericPower,
        { hours: 3, nowMs: nowBucket },
        SHARED_CACHE.spaceWeatherHpi,
    );
    useCachedQuery(
        api.magnetometer.getLatestStationReadings,
        {},
        SHARED_CACHE.spaceWeatherStationReadings,
    );

    // Render children immediately - cached data is available instantly
    return <>{children}</>;
}
